import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Camera, X, Loader2, RefreshCw, CheckCircle2, ImageIcon, Keyboard, Search } from 'lucide-react';
import useCameraViewport from '../utils/useCameraViewport';

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const BarcodeScanner = ({ onScanSuccess, onScanError, onClose, language = 'fr' }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('checking');
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [isiOSDevice] = useState(isIOS());
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const scannerRef = useRef(null);
  const containerRef = useRef(null);

  // Iter. 406.d — iOS-safe full-viewport shell (dvh + safe-area + scroll lock)
  const { containerStyle } = useCameraViewport({ open: true });
  const fileInputRef = useRef(null);
  const timeoutRef = useRef(null);

  // ===================== PHOTO CAPTURE =====================
  const handlePhotoCapture = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setProcessingPhoto(true);
    setError(null);
    setScanSuccess(false);

    try {
      // Try native BarcodeDetector first (Safari 17.2+, Chrome 83+)
      if ('BarcodeDetector' in window) {
        try {
          const bitmap = await createImageBitmap(file);
          const detector = new window.BarcodeDetector({
            formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e']
          });
          const barcodes = await detector.detect(bitmap);
          if (barcodes.length > 0) {
            setScanSuccess(true);
            setProcessingPhoto(false);
            setTimeout(() => onScanSuccess(barcodes[0].rawValue, { format: 'native_detector' }), 600);
            return;
          }
        } catch (nativeErr) {
        }
      }

      // Fallback: html5-qrcode scanFile
      const tempId = 'barcode-photo-scanner';
      let tempEl = document.getElementById(tempId);
      if (!tempEl) {
        tempEl = document.createElement('div');
        tempEl.id = tempId;
        tempEl.style.display = 'none';
        document.body.appendChild(tempEl);
      }

      const html5QrCode = new Html5Qrcode(tempId);
      const result = await html5QrCode.scanFile(file, true);
      html5QrCode.clear();

      setScanSuccess(true);
      setProcessingPhoto(false);
      setTimeout(() => onScanSuccess(result, { format: 'photo' }), 600);
    } catch (err) {
      console.error('Photo scan error:', err);
      setError(language === 'fr'
        ? 'Code-barres non détecté. Essaie le mode manuel ci-dessous.'
        : 'Barcode not detected. Try manual entry below.');
      setProcessingPhoto(false);
      setShowManualEntry(true);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [language, onScanSuccess]);

  const openPhotoCapture = useCallback(() => {
    setError(null);
    fileInputRef.current?.click();
  }, []);

  // ===================== MANUAL BARCODE ENTRY =====================
  const handleManualSubmit = useCallback(() => {
    const code = manualBarcode.trim();
    if (!code || code.length < 4) {
      setError(language === 'fr' ? 'Entre un code-barres valide (min 4 chiffres)' : 'Enter a valid barcode (min 4 digits)');
      return;
    }
    setScanSuccess(true);
    setTimeout(() => onScanSuccess(code, { format: 'manual' }), 600);
  }, [manualBarcode, language, onScanSuccess]);

  // ===================== CAMERA WITH TIMEOUT =====================
  const requestCameraPermission = useCallback(async (forceRetry = false) => {
    if (isiOSDevice) {
      setPermissionStatus('ios_photo_mode');
      return;
    }

    setPermissionStatus('checking');
    setError(null);

    try {
      // Permission check
      if (navigator.permissions?.query) {
        try {
          const permResult = await navigator.permissions.query({ name: 'camera' });
          if (permResult.state === 'denied' && !forceRetry) {
            setPermissionStatus('denied');
            setError(language === 'fr'
              ? 'Camera bloquee. Utilise le mode photo ou entre le code manuellement.'
              : 'Camera blocked. Use photo mode or enter code manually.');
            setShowManualEntry(true);
            return;
          }
        } catch { }
      }

      // getUserMedia with 3-second timeout
      const mediaPromise = navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('TIMEOUT')), 3000)
      );

      const stream = await Promise.race([mediaPromise, timeoutPromise]);
      stream.getTracks().forEach(track => track.stop());
      setPermissionStatus('granted');

      const devices = await Html5Qrcode.getCameras();
      if (devices?.length) {
        setCameras(devices);
        const backCamera = devices.find(d =>
          d.label.toLowerCase().includes('back') ||
          d.label.toLowerCase().includes('rear') ||
          d.label.toLowerCase().includes('arrière')
        );
        setSelectedCamera(backCamera?.id || devices[0].id);
      } else {
        setPermissionStatus('denied');
        setError(language === 'fr' ? 'Aucune camera détectée. Utilise le mode photo.' : 'No camera detected. Use photo mode.');
        setShowManualEntry(true);
      }
    } catch (err) {
      console.error('Camera permission error:', err);
      setPermissionStatus('denied');
      setShowManualEntry(true);

      if (err.message === 'TIMEOUT') {
        setError(language === 'fr'
          ? 'La camera ne repond pas. Utilise le mode photo ou entre le code manuellement.'
          : 'Camera not responding. Use photo mode or enter the code manually.');
      } else if (err.name === 'NotAllowedError') {
        setError(language === 'fr'
          ? 'Permission camera refusée. Utilise le mode photo.'
          : 'Camera permission denied. Use photo mode.');
      } else if (err.name === 'NotReadableError' || err.name === 'AbortError') {
        setError(language === 'fr'
          ? 'Camera utilisee par une autre app. Prends une photo a la place.'
          : 'Camera used by another app. Take a photo instead.');
      } else {
        setError(language === 'fr'
          ? 'Erreur camera. Utilise le mode photo ci-dessous.'
          : 'Camera error. Use photo mode below.');
      }
    }
  }, [isiOSDevice, language]);

  useEffect(() => {
    requestCameraPermission();
    return () => {
      stopScanner();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-start scanner when camera is granted and selected
  useEffect(() => {
    if (permissionStatus === 'granted' && selectedCamera && !isScanning && !scanSuccess) {
      startScanner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionStatus, selectedCamera]);
  const startScanner = useCallback(async () => {
    if (!selectedCamera) return;
    try {
      setError(null);
      setIsScanning(true);

      scannerRef.current = new Html5Qrcode('barcode-scanner-region');

      // OPTIMIZED: Only food barcode formats, lower fps
      const config = {
        fps: 5,
        qrbox: { width: 250, height: 120 },
        formatsToSupport: [0, 1, 2, 3],  // EAN_13, EAN_8, UPC_A, UPC_E only
        disableFlip: true,
      };

      // Auto-stop after 20 seconds to prevent battery drain
      timeoutRef.current = setTimeout(() => {
        if (scannerRef.current) {
          stopScanner();
          setError(language === 'fr'
            ? 'Scan expire. Prends une photo ou entre le code manuellement.'
            : 'Scan timed out. Take a photo or enter the code manually.');
          setShowManualEntry(true);
        }
      }, 20000);

      await scannerRef.current.start(
        selectedCamera,
        config,
        (decodedText, decodedResult) => {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          stopScanner();
          setScanSuccess(true);
          setTimeout(() => onScanSuccess(decodedText, decodedResult), 600);
        },
        () => { }
      );
    } catch (err) {
      console.error('Scanner start error:', err);
      setIsScanning(false);
      setShowManualEntry(true);
      setError(language === 'fr'
        ? 'Scanner impossible. Prends une photo ou entre le code.'
        : 'Scanner failed. Take a photo or enter the code.');
    }
  }, [selectedCamera, language, onScanSuccess]);

  const stopScanner = useCallback(async () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch { }
      scannerRef.current = null;
    }
    setIsScanning(false);
  }, []);

  const switchCamera = useCallback(() => {
    if (cameras.length <= 1) return;
    const idx = cameras.findIndex(c => c.id === selectedCamera);
    setSelectedCamera(cameras[(idx + 1) % cameras.length].id);
    if (isScanning) stopScanner().then(() => setTimeout(startScanner, 500));
  }, [cameras, selectedCamera, isScanning, stopScanner, startScanner]);

  const handleClose = useCallback(() => {
    stopScanner();
    onClose();
  }, [stopScanner, onClose]);

  // ===================== MANUAL ENTRY UI =====================
  const renderManualEntry = () => (
    <div className="w-full max-w-sm mt-4" data-testid="manual-barcode-entry">
      <div className="p-4 bg-secondary/50 rounded-xl border border-border">
        <div className="flex items-center gap-2 mb-3">
          <Keyboard className="w-4 h-4 text-orange-500" />
          <span className="text-sm font-bold">
            {language === 'fr' ? 'Entrer le code manuellement' : 'Enter code manually'}
          </span>
        </div>
        <div className="flex gap-2">
          <Input
            type="tel"
            inputMode="numeric"
            value={manualBarcode}
            onChange={(e) => setManualBarcode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
            placeholder="0 123456 789012"
            className="flex-1 bg-background text-lg tracking-wider font-mono"
            autoFocus={showManualEntry}
            data-testid="manual-barcode-input"
          />
          <Button
            onClick={handleManualSubmit}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4"
            data-testid="manual-barcode-submit"
          >
            <Search className="w-5 h-5" />
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">
          {language === 'fr'
            ? 'Le numero se trouve sous les barres du code-barres'
            : 'The number is under the barcode lines'}
        </p>
      </div>
    </div>
  );

  // ===================== iOS PHOTO MODE =====================
  const renderIOSPhotoMode = () => (
    <div className="text-center max-w-sm px-4">
      <div className="w-24 h-24 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <Camera className="w-12 h-12 text-primary" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">
        {language === 'fr' ? 'Scanner un code-barres' : 'Scan a barcode'}
      </h3>
      <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
        {language === 'fr'
          ? 'Prends une photo du code-barres. Detection automatique.'
          : 'Take a photo of the barcode. Automatic detection.'}
      </p>

      {error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-4">
          <p className="text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="space-y-3">
        <Button
          onClick={openPhotoCapture}
          disabled={processingPhoto}
          className="w-full bg-primary text-primary-foreground h-12 text-base font-semibold"
          size="lg"
          data-testid="ios-photo-capture-btn"
        >
          {processingPhoto ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Camera className="w-5 h-5 mr-2" />
          )}
          {processingPhoto
            ? (language === 'fr' ? 'Analyse en cours...' : 'Analyzing...')
            : (language === 'fr' ? 'Ouvrir la camera' : 'Open Camera')}
        </Button>

        <Button
          variant="outline"
          className="w-full"
          onClick={() => setShowManualEntry(!showManualEntry)}
          data-testid="ios-manual-toggle"
        >
          <Keyboard className="w-4 h-4 mr-2" />
          {language === 'fr' ? 'Entrer le code manuellement' : 'Enter code manually'}
        </Button>
      </div>

      {showManualEntry && renderManualEntry()}
    </div>
  );

  // ===================== RENDER =====================
  return (
    <div
      className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm"
      style={containerStyle}
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
          <h2 className="font-heading text-lg uppercase text-foreground">
            {language === 'fr' ? 'Scanner un code-barres' : 'Scan Barcode'}
          </h2>
          <Button variant="ghost" size="icon" onClick={handleClose} data-testid="close-scanner-btn">
            <X className="w-6 h-6" />
          </Button>
        </div>

        {/* Scanner Area */}
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center p-4 overflow-y-auto">
          {scanSuccess ? (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <CheckCircle2 className="w-10 h-10 text-green-500" />
              </div>
              <p className="text-green-500 font-semibold text-lg">
                {language === 'fr' ? 'Code-barres détecté !' : 'Barcode detected!'}
              </p>
            </div>
          ) : processingPhoto ? (
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">
                {language === 'fr' ? 'Analyse de la photo...' : 'Analyzing photo...'}
              </p>
            </div>
          ) : isiOSDevice || permissionStatus === 'ios_photo_mode' ? (
            renderIOSPhotoMode()
          ) : permissionStatus === 'checking' ? (
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">
                {language === 'fr' ? 'Acces a la camera...' : 'Accessing camera...'}
              </p>
            </div>
          ) : permissionStatus === 'denied' || error ? (
            <div className="text-center max-w-sm">
              <div className="w-20 h-20 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Camera className="w-10 h-10 text-destructive" />
              </div>
              {error && <p className="text-destructive mb-4 text-sm">{error}</p>}

              <div className="flex flex-col gap-3">
                <Button
                  onClick={openPhotoCapture}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={processingPhoto}
                  data-testid="photo-capture-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Prendre une photo' : 'Take photo'}
                </Button>

                <Button onClick={() => requestCameraPermission(true)} variant="outline" data-testid="retry-permission-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Reessayer le scan' : 'Retry scan'}
                </Button>
              </div>

              {renderManualEntry()}
            </div>
          ) : (
            <>
              {/* Live scanner (Android/Desktop) */}
              <div
                id="barcode-scanner-region"
                ref={containerRef}
                className="w-full max-w-md aspect-[4/3] bg-black rounded-xl overflow-hidden relative"
              >
                {!isScanning && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <Camera className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-sm opacity-70">
                        {language === 'fr' ? 'Appuie sur Demarrer' : 'Press Start'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-5 flex-wrap justify-center">
                {!isScanning ? (
                  <Button
                    onClick={startScanner}
                    className="bg-primary text-primary-foreground font-heading uppercase"
                    size="lg"
                    data-testid="start-scanner-btn"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    {language === 'fr' ? 'Demarrer le scan' : 'Start Scanning'}
                  </Button>
                ) : (
                  <Button onClick={stopScanner} variant="destructive" size="lg" data-testid="stop-scanner-btn">
                    <X className="w-5 h-5 mr-2" />
                    {language === 'fr' ? 'Arreter' : 'Stop'}
                  </Button>
                )}

                {cameras.length > 1 && (
                  <Button onClick={switchCamera} variant="outline" size="lg" data-testid="switch-camera-btn">
                    <RefreshCw className="w-5 h-5" />
                  </Button>
                )}

                <Button onClick={openPhotoCapture} variant="outline" size="lg" data-testid="photo-fallback-btn">
                  <ImageIcon className="w-5 h-5 mr-2" />
                  Photo
                </Button>
              </div>

              {/* Manual entry always visible below scanner */}
              {renderManualEntry()}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border text-center">
          <p className="text-xs text-muted-foreground">
            EAN-13 · EAN-8 · UPC-A · UPC-E
          </p>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handlePhotoCapture}
        className="hidden"
        data-testid="barcode-photo-input"
      />
    </div>
  );
};

export default BarcodeScanner;
